import React, { createContext, useContext, useEffect, ReactNode, useMemo } from "react";
import { analyticsClient } from "sdks/analytics";
import { useAuth } from "hooks/useAuth";

interface AnalyticsContextType {
    isInitialized: boolean;
}

const AnalyticsContext = createContext<AnalyticsContextType>({
    isInitialized: false,
});

interface AnalyticsProviderProps {
    children: ReactNode;
    sessionId?: string;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
    const [isInitialized, setIsInitialized] = React.useState(false);
    const { isAuthenticated, profileDetails } = useAuth();

    useEffect(() => {
        setIsInitialized(true);
        if (isAuthenticated) {
            analyticsClient.setSessionId(localStorage.getItem("session_info"));
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated || !profileDetails) {
            analyticsClient.setUser(null);
            return;
        }

        if (profileDetails.id) {
            analyticsClient.setUser(profileDetails.id);
        }
    }, [isAuthenticated, profileDetails]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            analyticsClient.destroy();
        };
    }, []);

    const value = useMemo(() => ({ isInitialized }), [isInitialized]);
    return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
};

export const useAnalyticsContext = () => {
    const context = useContext(AnalyticsContext);
    if (!context) {
        throw new Error("useAnalyticsContext must be used within an AnalyticsProvider");
    }
    return context;
};
